import { useCallback } from 'react';
import { AssociatableObjectTypesMap } from 'campaigns-lib/constants/AssetTypes';
import { saveCampaign } from '../api/CampaignsAssociation';
import { useCampaignsContext } from '../CampaignsProvider';
import { SENTRY_KEYS } from 'campaigns-lib/constants/sentry';
import Raven from 'raven-js';
import { useDataFetchingClient } from 'data-fetching-client';
import { GET_ASSOCIATED_CAMPAIGN_FIELD_NAME } from './useFetchAssociatedCampaign';
import { useSetAssociationsMutation } from 'campaigns-lib/__generated__/chirp/com/hubspot/campaigns/association/rpc/CampaignsAssociationRpc.hooks';
import { useDebounceCallback } from '../../hooks/useDebounceCallback';
import { CACHE_DEBOUNCE_DELAY } from '../../constants/campaignSelect';
export function useHandleCampaignChange({
  objectType,
  onChange = () => {},
  save = true,
  selectedCampaignGuids,
  selectedAssetCrmId,
  setSelectedCampaignGuids,
  onSuccess = () => {},
  onFailure = () => {}
}) {
  const {
    isOneToMany,
    setUpdatedCampaigns
  } = useCampaignsContext();
  const client = useDataFetchingClient();
  const [setAssociations] = useSetAssociationsMutation();
  const debouncedCacheEvict = useDebounceCallback(() => client.cache.evict({
    fieldName: GET_ASSOCIATED_CAMPAIGN_FIELD_NAME
  }), CACHE_DEBOUNCE_DELAY);
  return useCallback(({
    target: {
      value: updatedCampaignGuids
    }
  }, selectOptions, selectedOption) => {
    const updatedCampaignGuidsArray = Array.isArray(updatedCampaignGuids) ? updatedCampaignGuids : [updatedCampaignGuids];
    const lastSelectedCampaign = updatedCampaignGuidsArray.length > 0 ? updatedCampaignGuidsArray[updatedCampaignGuidsArray.length - 1] : undefined;
    if (save && selectedAssetCrmId) {
      if (objectType === AssociatableObjectTypesMap.MARKETING_EMAIL && isOneToMany) {
        setAssociations({
          variables: {
            assetObjectTypeId: objectType,
            crmAssetIds: [Number(selectedAssetCrmId)],
            campaignGuids: updatedCampaignGuidsArray.filter(Boolean),
            primaryCampaignGuid: lastSelectedCampaign
          }
        }).then(() => {
          debouncedCacheEvict();
          onSuccess({
            updatedCampaignGuids: updatedCampaignGuidsArray,
            selectedOption
          });
        }).catch(error => {
          onFailure();
          Raven.captureException(error, {
            fingerprint: [SENTRY_KEYS.CAMPAIGNS_PLATFORM_UPDATE_FAILED],
            tags: {
              lib: 'campaigns-lib'
            }
          });
        });
      } else {
        saveCampaign({
          objectType,
          selectedAssetCrmId,
          currentCampaignGuids: selectedCampaignGuids,
          updatedCampaignGuids: updatedCampaignGuidsArray,
          onSuccess: () => onSuccess({
            updatedCampaignGuids: updatedCampaignGuidsArray,
            selectedOption
          }),
          onFailure
        }).then(() => {
          debouncedCacheEvict();
        }).catch(error => {
          Raven.captureException(error, {
            fingerprint: [SENTRY_KEYS.CAMPAIGNS_PLATFORM_UPDATE_FAILED],
            tags: {
              lib: 'campaigns-lib'
            }
          });
        });
      }
    }

    // Save the updated campaign to the context to be saved outside of the select.
    setUpdatedCampaigns(selectOptions.map(({
      guid,
      objectId
    }) => ({
      campaignGuid: guid,
      objectId: objectId
    })));

    // Update the selected campaign guid in the select.
    setSelectedCampaignGuids(updatedCampaignGuidsArray);

    // Invoke the select callback prop for consumers to use.
    onChange === null || onChange === void 0 || onChange((selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.guid) || null);
  }, [debouncedCacheEvict, isOneToMany, objectType, onChange, onFailure, onSuccess, save, selectedAssetCrmId, selectedCampaignGuids, setAssociations, setSelectedCampaignGuids, setUpdatedCampaigns]);
}
export default useHandleCampaignChange;